import React from "react";
import { Award, Users, Globe, Heart } from "lucide-react";

const features = [
  {
    icon: Award,
    title: "Premium Service",
    description: "Award-winning travel experiences curated by experts",
  },
  {
    icon: Users,
    title: "Personal Concierge",
    description: "Dedicated travel specialists at your service 24/7",
  },
  {
    icon: Globe,
    title: "Global Network",
    description: "Exclusive partnerships with luxury properties worldwide",
  },
  {
    icon: Heart,
    title: "Tailored Journeys",
    description: "Customized itineraries designed for your preferences",
  },
];

export const AboutSection = () => {
  return (
    <section
      id="about"
      className="py-20 lg:py-32 bg-background relative overflow-hidden"
    >
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-block text-primary text-sm font-semibold tracking-wider uppercase mb-3">
              About ELEV8 Travel
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Redefining{" "}
              <span className="text-gradient-gold">Luxury Travel</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-secondary via-accent to-secondary mb-8" />

            <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
              At ELEV8 Travel, we believe that extraordinary journeys deserve
              extraordinary attention to detail. For over 15 years, we&apos;ve been
              crafting bespoke travel experiences for discerning travelers who
              seek more than just a vacation.
            </p>

            <p className="text-muted-foreground mb-8 leading-relaxed">
              Our team of luxury travel specialists combines insider knowledge,
              exclusive partnerships, and impeccable service to transform your
              travel dreams into unforgettable realities. From private island
              retreats to cultural immersions, we curate every moment of your
              journey.
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-6">
              {[
                { value: "15+", label: "Years Experience" },
                { value: "98%", label: "Client Satisfaction" },
                { value: "50+", label: "Countries" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-4 rounded-xl bg-muted/50 border border-border"
                >
                  <div className="text-2xl font-bold text-gradient-gold mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Feature Grid */}
          <div className="grid grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`p-6 rounded-2xl luxury-card group ${
                  index === 1 || index === 2 ? "mt-8" : ""
                }`}
              >
                <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center mb-4 group-hover:bg-secondary/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
